/**
 * Декор дизайн-системы: небо дня, рукописное подчёркивание, скотч.
 *
 * Это не украшательство ради украшательства. «Небо» отвечает на вопрос
 * «какое сейчас время дня» ещё до того, как человек прочитал хоть строчку —
 * в приложении про день ребёнка утро и вечер правда разные. Подчёркивание
 * от руки отделяет разделы, не добавляя ни одной серой линии.
 */

import clsx from 'clsx'
import { useEffect, useState } from 'react'

import { skyForHour, type SkyPhase } from '@/shared/ui/sky'

/* ─── Небо дня ──────────────────────────────────────────────────────────── */

/**
 * Тёплая заливка за шапкой экрана. Живёт в потоке, а не fixed: при скролле
 * должна уезжать вместе с заголовком, иначе выглядит как наклейка на стекле.
 */
export function TimeSky({ className }: { className?: string }) {
  const [phase, setPhase] = useState<SkyPhase>(() => skyForHour(new Date().getHours()))

  // Пересчитываем раз в пять минут: приложение часто висит открытым весь день,
  // и «утро» в шесть вечера выглядело бы поломкой.
  useEffect(() => {
    const id = setInterval(() => setPhase(skyForHour(new Date().getHours())), 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div
      aria-hidden
      className={clsx(
        'pointer-events-none absolute inset-x-0 top-0 h-[260px] transition-[background] duration-1000',
        className,
      )}
      style={{ background: `var(--sky-${phase})` }}
    />
  )
}

/* ─── Подчёркивание от руки ─────────────────────────────────────────────── */

/**
 * Детерминированный поток случайных чисел от строки.
 *
 * Именно детерминированный, а не Math.random: подпись раздела перерисовывается
 * на каждое обновление данных экрана, и живой рандом заставлял бы линию
 * дёргаться под заголовком при каждом ответе сервера. Одна и та же подпись
 * должна всегда давать одну и ту же линию — но у разных подписей линии разные.
 */
function seeded(text: string): () => number {
  let hash = 2166136261
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return () => {
    hash += 0x6d2b79f5
    let t = hash
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Высота системы координат линии. Волна гуляет вокруг середины.
const BAND = 6
// Примерная ширина заглавной буквы подписи: 13px кегль плюс трекинг 0.08em.
// Точность тут не нужна — по этой оценке считается только частота волн,
// а сама линия растягивается на фактическую ширину заголовка.
const CHAR_WIDTH = 9.5
// Длина одной волны. Меньше — линия становится мелкой рябью, больше — вялой.
const WAVE = 20

/** Построить волнистый путь: чуть разные амплитуды и плавный увод базы. */
function squigglePath(width: number, random: () => number): string {
  const waves = Math.max(2, Math.round(width / WAVE))
  const step = width / waves

  let y = BAND / 2 + (random() - 0.5) * 0.5
  let up = random() > 0.5
  let path = `M0 ${y.toFixed(2)}`

  for (let i = 0; i < waves; i += 1) {
    // Амплитуда гуляет: у линии, проведённой от руки, волны неодинаковы.
    const amplitude = (1.15 + random() * 0.85) * (up ? -1 : 1)
    // База медленно уводится, поэтому линия не строго горизонтальна.
    const next = y + (random() - 0.5) * 0.5
    const x = step * (i + 1)
    path += ` Q${(x - step / 2).toFixed(2)} ${(y + amplitude).toFixed(2)} ${x.toFixed(2)} ${next.toFixed(2)}`
    y = next
    up = !up
  }
  return path
}

/**
 * Волнистая линия под подписью раздела, которая «дорисовывается» при появлении.
 *
 * Длина — по ширине заголовка: линия подчёркивает слово, а не отрезок в
 * семьдесят пикселей рядом с ним. Рисунок свой у каждой подписи, но у одной и
 * той же подписи он постоянен.
 */
export function Squiggle({
  seed = '',
  className,
  tone = 'accent',
}: {
  /** Текст, под которым идёт линия: он же задаёт её рисунок и длину. */
  seed?: string
  className?: string
  tone?: 'accent' | 'warm' | 'honey' | 'muted'
}) {
  const color = {
    accent: 'var(--color-accent)',
    warm: 'var(--color-warm)',
    honey: 'var(--color-honey)',
    muted: 'var(--color-line)',
  }[tone]

  const width = Math.max(40, seed.length * CHAR_WIDTH)
  const path = squigglePath(width, seeded(seed))

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${width.toFixed(0)} ${BAND}`}
      preserveAspectRatio="none"
      className={clsx('h-[5px] w-full', className)}
    >
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.55"
        // pathLength нормирует длину к единице, поэтому одна и та же анимация
        // дорисовки работает для линии любой длины.
        pathLength={1}
        style={{
          strokeDasharray: 1,
          // @ts-expect-error — своя переменная для @keyframes draw
          '--dash': 1,
          animation: 'draw 900ms var(--ease-out-soft) both',
        }}
      />
    </svg>
  )
}

/* ─── Скотч ─────────────────────────────────────────────────────────────── */

/**
 * Обёртка «приклеено в альбом»: лёгкий наклон и полоска скотча сверху.
 * Наклон детерминированный (по индексу), чтобы при перерисовке список
 * не «дёргался» новыми случайными углами.
 */
export function Taped({
  index = 0,
  className,
  children,
}: {
  index?: number
  className?: string
  children: React.ReactNode
}) {
  const tilt = [-1.6, 1.2, -0.9, 1.7, -1.3][index % 5]
  return (
    <div
      className={clsx('taped', className)}
      style={{ '--tilt': `${tilt}deg` } as React.CSSProperties}
    >
      {children}
    </div>
  )
}
